import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage = 'https://public.readdy.ai/ai/img_res/dddcdb8a-4f07-441e-8e3f-1ae6a286ee38.png',
  schema,
}: SEOProps) => {
  useEffect(() => {
    // Set title
    document.title = title;

    // Set or update meta tags
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    setMetaTag('description', description);
    if (keywords) {
      setMetaTag('keywords', keywords);
    }

    // Open Graph tags
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:image', ogImage, true);
    
    if (canonical) {
      setMetaTag('og:url', canonical, true);
    }

    // Twitter tags
    setMetaTag('twitter:card', 'summary_large_image', true);
    setMetaTag('twitter:title', title, true);
    setMetaTag('twitter:description', description, true);
    setMetaTag('twitter:image', ogImage, true);
    
    if (canonical) {
      setMetaTag('twitter:url', canonical, true);
    }

    // Canonical link
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]');
      
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.setAttribute('rel', 'canonical');
        document.head.appendChild(linkElement);
      }
      
      linkElement.setAttribute('href', canonical);
    }

    // Schema.org JSON-LD
    if (schema) {
      const scriptId = 'schema-org-jsonld';
      let scriptElement = document.getElementById(scriptId);
      
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.id = scriptId;
        scriptElement.type = 'application/ld+json';
        document.head.appendChild(scriptElement);
      }
      
      scriptElement.textContent = JSON.stringify(schema);
    }

    // Update last-modified
    const lastModified = new Date().toISOString().split('T')[0];
    setMetaTag('last-modified', lastModified);
  }, [title, description, keywords, canonical, ogType, ogImage, schema]);
};

// Helper function to generate schema
export const generateSchema = {
  organization: (siteUrl: string) => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Yoshino Village Network',
    url: siteUrl,
    logo: 'https://public.readdy.ai/ai/img_res/dddcdb8a-4f07-441e-8e3f-1ae6a286ee38.png',
    description: '奈良・吉野で地域共創を進めるコミュニティ。相談・参加・視察受け入れを行っています。',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '吉野町',
      addressRegion: '奈良県',
      addressCountry: 'JP',
    },
    sameAs: [
      'https://www.instagram.com/yoshino_village_project/',
      'https://www.youtube.com/@YoshinoNowVoice',
    ],
  }),

  webpage: (siteUrl: string, title: string, description: string) => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: siteUrl,
    publisher: {
      '@type': 'Organization',
      name: 'Yoshino Village Network',
      logo: {
        '@type': 'ImageObject',
        url: 'https://public.readdy.ai/ai/img_res/dddcdb8a-4f07-441e-8e3f-1ae6a286ee38.png',
      },
    },
  }),

  article: (siteUrl: string, title: string, description: string, datePublished: string, imageUrl: string) => ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    url: siteUrl,
    datePublished: datePublished,
    dateModified: new Date().toISOString(),
    author: {
      '@type': 'Organization',
      name: 'Yoshino Village Network',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Yoshino Village Network',
      logo: {
        '@type': 'ImageObject',
        url: 'https://public.readdy.ai/ai/img_res/dddcdb8a-4f07-441e-8e3f-1ae6a286ee38.png',
      },
    },
    image: {
      '@type': 'ImageObject',
      url: imageUrl,
    },
  }),

  collectionPage: (siteUrl: string, title: string, description: string) => ({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description: description,
    url: siteUrl,
    publisher: {
      '@type': 'Organization',
      name: 'Yoshino Village Network',
    },
  }),
};
